import React, { useEffect, useMemo, useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import NotificationDropdown from './NotificationDropdown';
import LanguageSwitcher from './LanguageSwitcher';
import Logo from './Logo';
import { ADMIN_MENU } from './adminMenu';
import { getDashboardOverview } from '../services/api';
import '../styles/dashboard-theme.css';
import '../styles/admin-ui.css';
import './DashboardLayout.css';

const truthyFlag = (value) => value === true || value === 'true' || value === 1 || value === '1';

const DashboardLayout = () => {
  const { t } = useTranslation();
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [showMobileMore, setShowMobileMore] = useState(false);
  const [companyPlan, setCompanyPlan] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, user, isCompanyAdmin, isSystemAdmin } = useAuth();

  const systemAdmin = useMemo(
    () =>
      truthyFlag(isSystemAdmin) ||
      truthyFlag(user?.is_system_admin) ||
      truthyFlag(localStorage.getItem('isSystemAdmin')),
    [isSystemAdmin, user]
  );

  const companyAdmin = useMemo(
    () =>
      truthyFlag(isCompanyAdmin) ||
      truthyFlag(user?.is_company_admin) ||
      truthyFlag(localStorage.getItem('isCompanyAdmin')),
    [isCompanyAdmin, user]
  );

  useEffect(() => {
    let cancelled = false;
    getDashboardOverview()
      .then((data) => {
        if (!cancelled) setCompanyPlan(String(data?.plan_type || '').toLowerCase());
      })
      .catch(() => {
        if (!cancelled) setCompanyPlan('');
      });
    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  const planDisplay = useMemo(() => {
    const key = companyPlan === 'premium' ? 'pro' : companyPlan || 'free';
    if (key === 'pro') return t('upgrade.plans.pro', 'Pro');
    if (key === 'basic') return t('upgrade.plans.basic', 'Basic');
    if (key === 'enterprise') return t('upgrade.plans.enterprise', 'Enterprise');
    if (key === 'free') return t('upgrade.plans.free', 'Free');
    return key.charAt(0).toUpperCase() + key.slice(1);
  }, [companyPlan, t]);

  const isAdminArea =
    location.pathname === '/dashboard/admin' || location.pathname.startsWith('/admin/');
  const isUpgradeArea = location.pathname === '/dashboard/upgrade';

  const handleLogoutClick = () => {
    setShowMobileMore(false);
    setShowLogoutConfirm(true);
  };

  const handleLogoutConfirm = () => {
    setShowLogoutConfirm(false);
    logout();
    navigate('/');
  };

  const icons = {
    dashboard: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="3" width="7" height="9" rx="1" />
        <rect x="14" y="3" width="7" height="5" rx="1" />
        <rect x="14" y="12" width="7" height="9" rx="1" />
        <rect x="3" y="16" width="7" height="5" rx="1" />
      </svg>
    ),
    connections: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="6" cy="6" r="3" />
        <circle cx="18" cy="18" r="3" />
        <path d="M8.5 8.5l7 7" />
        <circle cx="18" cy="6" r="3" />
        <circle cx="6" cy="18" r="3" />
        <path d="M15.5 8.5l-7 7" />
      </svg>
    ),
    downloads: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 3v12m0 0l-4-4m4 4l4-4" />
        <path d="M3 15v4a2 2 0 002 2h14a2 2 0 002-2v-4" />
      </svg>
    ),
    company: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 21h18" />
        <path d="M5 21V7l8-4v18" />
        <path d="M19 21V11l-6-4" />
        <path d="M9 9v.01M9 12v.01M9 15v.01M9 18v.01" />
      </svg>
    ),
    admin: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="3" />
        <path d="M12 1v4m0 14v4M4.22 4.22l2.83 2.83m9.9 9.9l2.83 2.83M1 12h4m14 0h4M4.22 19.78l2.83-2.83m9.9-9.9l2.83-2.83" />
      </svg>
    ),
    user: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="8" r="4" />
        <path d="M4 20c0-4 4-6 8-6s8 2 8 6" />
      </svg>
    ),
    logout: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4" />
        <polyline points="16,17 21,12 16,7" />
        <line x1="21" y1="12" x2="9" y2="12" />
      </svg>
    ),
    chevronLeft: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="15,18 9,12 15,6" />
      </svg>
    ),
    chevronRight: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="9,6 15,12 9,18" />
      </svg>
    ),
    ticket: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 002 2 2 2 0 010 4 2 2 0 00-2 2v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 00-2-2 2 2 0 010-4 2 2 0 002-2V7a2 2 0 00-2-2H5z" />
      </svg>
    ),
    plan: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 3l2.4 4.9 5.4.8-3.9 3.8.9 5.4L12 15.3 7.2 18l.9-5.4L4.2 8.7l5.4-.8L12 3z" />
      </svg>
    ),
    more: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="5" cy="12" r="1.5" />
        <circle cx="12" cy="12" r="1.5" />
        <circle cx="19" cy="12" r="1.5" />
      </svg>
    ),
  };

  const menuItems = [
    { path: '/dashboard', label: t('sidebar.dashboard', 'Dashboard'), short: t('sidebar.short.home', 'Home'), icon: icons.dashboard, roles: ['all'], mobilePrimary: true },
    { path: '/dashboard/connections', label: t('sidebar.connections', 'Connections'), short: t('sidebar.short.links', 'Links'), icon: icons.connections, roles: ['all'], mobilePrimary: true },
    { path: '/dashboard/downloads', label: t('sidebar.downloads', 'Downloads'), short: t('sidebar.short.apps', 'Apps'), icon: icons.downloads, roles: ['all'], mobilePrimary: true },
    { path: '/dashboard/tickets', label: t('sidebar.tickets', 'Support'), short: t('sidebar.short.help', 'Help'), icon: icons.ticket, roles: ['all'], mobilePrimary: true },
    { path: '/dashboard/company', label: t('sidebar.company', 'My Company'), short: t('sidebar.short.company', 'Company'), icon: icons.company, roles: ['company_admin'] },
    { path: '/dashboard/admin', label: t('sidebar.admin', 'System Admin'), short: t('sidebar.short.admin', 'Admin'), icon: icons.admin, roles: ['system_admin'] },
  ];

  const filteredMenuItems = menuItems.filter((item) => {
    if (item.roles.includes('all')) return true;
    if (item.roles.includes('company_admin') && companyAdmin) return true;
    if (item.roles.includes('system_admin') && systemAdmin) return true;
    return false;
  });

  const mobilePrimaryItems = filteredMenuItems.filter((item) => item.mobilePrimary);
  const mobileMoreItems = filteredMenuItems.filter((item) => !item.mobilePrimary);

  const isNavActive = (path) => {
    if (path === '/dashboard/admin') return isAdminArea;
    if (path === '/dashboard') return location.pathname === '/dashboard';
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  const moreSheetActive =
    isUpgradeArea ||
    location.pathname === '/dashboard/profile' ||
    mobileMoreItems.some((item) => isNavActive(item.path));

  const goMobile = (path) => {
    setShowMobileMore(false);
    navigate(path);
  };

  return (
    <div className={`dashboard-layout ${showMobileMore ? 'mobile-more-open' : ''}`}>
      <aside className={`sidebar ${isSidebarOpen ? 'open' : 'closed'}`}>
        <div className="sidebar-header">
          <div className="sidebar-logo-wrapper" onClick={() => navigate('/dashboard')} style={{ cursor: 'pointer' }}>
            {isSidebarOpen ? (
              <Logo size="medium" variant="full" className="arc-logo-light" />
            ) : (
              <Logo size="small" variant="icon" className="arc-logo-light" />
            )}
          </div>
          <button
            className="sidebar-toggle"
            onClick={() => setIsSidebarOpen(!isSidebarOpen)}
            type="button"
          >
            {isSidebarOpen ? icons.chevronLeft : icons.chevronRight}
          </button>
        </div>

        <nav className="sidebar-nav">
          {filteredMenuItems.map((item) => {
            const isActive = isNavActive(item.path);

            return (
              <button
                key={item.path}
                type="button"
                className={`nav-item ${isActive ? 'active' : ''}`}
                onClick={() => navigate(item.path)}
              >
                <span className="nav-icon">{item.icon}</span>
                {isSidebarOpen && <span className="nav-label">{item.label}</span>}
              </button>
            );
          })}

        </nav>

        <div className="sidebar-footer">
          <button
            type="button"
            className={`sidebar-plan ${isUpgradeArea ? 'is-active' : ''}`}
            onClick={() => navigate('/dashboard/upgrade')}
            title={t('sidebar.premiumPackage', 'Company premium package')}
          >
            <span className="sidebar-plan-icon">{icons.plan}</span>
            {isSidebarOpen && (
              <span className="sidebar-plan-copy">
                <span className="sidebar-plan-kicker">
                  {t('sidebar.premiumPackage', 'Company premium package')}
                </span>
                <span className="sidebar-plan-name">{planDisplay}</span>
                <span className="sidebar-plan-cta">
                  {companyAdmin
                    ? t('sidebar.upgradeCta', 'Upgrade')
                    : t('sidebar.viewPlanCta', 'View package')}
                </span>
              </span>
            )}
          </button>
          {isSidebarOpen && (
            <div
              className="user-info"
              onClick={() => navigate('/dashboard/profile')}
              style={{ cursor: 'pointer' }}
            >
              <div className="user-avatar">{icons.user}</div>
              <div className="user-details">
                <p className="user-name">{user?.username}</p>
                <p className="user-role">{user?.role}</p>
              </div>
            </div>
          )}
          <button className="logout-btn" onClick={handleLogoutClick} title={t('sidebar.logout', 'Logout')} type="button">
            <span className="logout-icon">{icons.logout}</span>
            {isSidebarOpen && <span>{t('sidebar.logout', 'Çıkış Yap')}</span>}
          </button>
        </div>
      </aside>

      {showLogoutConfirm && (
        <div className="logout-confirm-overlay" onClick={() => setShowLogoutConfirm(false)} role="presentation">
          <div
            className="logout-confirm-modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="logout-confirm-title"
          >
            <h2 id="logout-confirm-title">{t('sidebar.logoutConfirmTitle', 'Log out?')}</h2>
            <p>{t('sidebar.logoutConfirm', 'Are you sure you want to log out?')}</p>
            <div className="logout-confirm-actions">
              <button type="button" className="logout-confirm-cancel" onClick={() => setShowLogoutConfirm(false)}>
                {t('common.cancel', 'Cancel')}
              </button>
              <button type="button" className="logout-confirm-yes" onClick={handleLogoutConfirm}>
                {t('sidebar.logout', 'Log out')}
              </button>
            </div>
          </div>
        </div>
      )}

      <main className={`main-content ${isSidebarOpen ? 'sidebar-open' : 'sidebar-closed'}`}>
        <header className="content-header">
          <div className="mobile-header-brand" onClick={() => goMobile('/dashboard')}>
            <Logo size="small" variant="icon" className="arc-logo-light" />
            <span>ArcVPN</span>
          </div>
          <div className="header-actions">
            <LanguageSwitcher variant="segmented" />
            <NotificationDropdown />
            <div
              className="user-greeting"
              onClick={() => navigate('/dashboard/profile')}
              style={{ cursor: 'pointer' }}
              title={t('sidebar.profile', 'Profile')}
            >
              <span className="user-greeting-icon">
                {(user?.username || 'U').charAt(0).toUpperCase()}
              </span>
              <span>{user?.username}</span>
            </div>
          </div>
        </header>
        <div className="content-body">
          {(isAdminArea || systemAdmin) && isAdminArea && (
            <nav className="aui-subnav" aria-label={t('systemAdmin.menuLabel', 'Admin modules')}>
              {ADMIN_MENU.map((item) => {
                const active =
                  item.path === '/dashboard/admin'
                    ? location.pathname === '/dashboard/admin'
                    : location.pathname === item.path ||
                      location.pathname.startsWith(`${item.path}/`);
                return (
                  <button
                    key={item.id}
                    type="button"
                    className={`aui-subnav-btn ${active ? 'is-active' : ''}`}
                    onClick={() => navigate(item.path)}
                  >
                    <span>{item.mark}</span>
                    {t(item.titleKey, item.title)}
                  </button>
                );
              })}
            </nav>
          )}
          <Outlet />
        </div>
      </main>

      <nav className="mobile-tabbar" aria-label={t('sidebar.mobileNav', 'Main navigation')}>
        {mobilePrimaryItems.map((item) => {
          const active = isNavActive(item.path);
          return (
            <button
              key={item.path}
              type="button"
              className={`mobile-tab ${active ? 'is-active' : ''}`}
              onClick={() => goMobile(item.path)}
            >
              <span className="mobile-tab-icon">{item.icon}</span>
              <span className="mobile-tab-label">{item.short || item.label}</span>
            </button>
          );
        })}
        <button
          type="button"
          className={`mobile-tab ${moreSheetActive || showMobileMore ? 'is-active' : ''}`}
          onClick={() => setShowMobileMore((open) => !open)}
          aria-expanded={showMobileMore}
        >
          <span className="mobile-tab-icon">{icons.more}</span>
          <span className="mobile-tab-label">{t('sidebar.more', 'More')}</span>
        </button>
      </nav>

      {showMobileMore && (
        <div className="mobile-more-overlay" onClick={() => setShowMobileMore(false)} role="presentation">
          <div
            className="mobile-more-sheet"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label={t('sidebar.more', 'More')}
          >
            <div className="mobile-more-handle" />
            <div className="mobile-more-user">
              <span className="mobile-more-avatar">{(user?.username || 'U').charAt(0).toUpperCase()}</span>
              <div>
                <strong>{user?.username}</strong>
                <em>{planDisplay} · {user?.role || 'member'}</em>
              </div>
            </div>

            <div className="mobile-more-grid">
              <button type="button" className={`mobile-more-item ${isUpgradeArea ? 'is-active' : ''}`} onClick={() => goMobile('/dashboard/upgrade')}>
                <span className="mobile-more-icon">{icons.plan}</span>
                <span>{t('sidebar.premiumPackage', 'Company package')}</span>
                <em>{planDisplay}</em>
              </button>
              <button type="button" className={`mobile-more-item ${location.pathname === '/dashboard/profile' ? 'is-active' : ''}`} onClick={() => goMobile('/dashboard/profile')}>
                <span className="mobile-more-icon">{icons.user}</span>
                <span>{t('sidebar.profile', 'Profile')}</span>
              </button>
              {mobileMoreItems.map((item) => (
                <button
                  key={item.path}
                  type="button"
                  className={`mobile-more-item ${isNavActive(item.path) ? 'is-active' : ''}`}
                  onClick={() => goMobile(item.path)}
                >
                  <span className="mobile-more-icon">{item.icon}</span>
                  <span>{item.label}</span>
                </button>
              ))}
            </div>

            <button type="button" className="mobile-more-logout" onClick={handleLogoutClick}>
              <span className="mobile-more-icon">{icons.logout}</span>
              {t('sidebar.logout', 'Log out')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardLayout;
